import { Link, useParams, Navigate } from "react-router-dom";
import { useMemo } from "react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import ArticleShare from "@/components/ArticleShare";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import { getCaseStudy, caseStudies } from "@/data/caseStudies";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

const Section = ({ id, eyebrow, heading, children }) => (
  <section
    id={id}
    style={{
      paddingBlock: "clamp(3rem, 6vw, 5rem)",
      borderTop: "1px solid var(--line)",
      scrollMarginTop: "100px",
    }}
    data-testid={`cs-section-${id}`}
  >
    <Reveal as="p" className="eyebrow">{eyebrow}</Reveal>
    <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem", fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}>
      {heading}
    </Reveal>
    {children}
  </section>
);

const Para = ({ children }) => (
  <p
    style={{
      color: "var(--ink-soft)",
      fontSize: "1.06rem",
      lineHeight: 1.75,
      margin: "0 0 1.1rem",
      maxWidth: "72ch",
    }}
  >
    {children}
  </p>
);

const NamedList = ({ items }) => (
  <div style={{ display: "grid", gap: "1.6rem", marginTop: ".6rem" }}>
    {items.map((it, i) => (
      <Reveal key={i} style={{ maxWidth: "72ch" }}>
        <h3 style={{ fontFamily: "var(--display)", fontSize: "1.15rem", margin: "0 0 .5rem", fontWeight: 500, color: "var(--ink)" }}>
          {it.head}
        </h3>
        <p style={{ color: "var(--ink-soft)", fontSize: "1rem", lineHeight: 1.7, margin: 0 }}>
          {it.body}
        </p>
      </Reveal>
    ))}
  </div>
);

export const CaseStudyDetail = () => {
  const { slug } = useParams();
  const data = getCaseStudy(slug);

  const { prev, next } = useMemo(() => {
    if (!data) return { prev: null, next: null };
    const sorted = [...caseStudies].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((c) => c.slug === data.slug);
    return { prev: idx > 0 ? sorted[idx - 1] : null, next: idx < sorted.length - 1 ? sorted[idx + 1] : null };
  }, [data]);

  useSeo({
    title: data ? (data.metaTitle || data.title) : "Case study",
    description: data?.metaDescription,
    path: data ? `/case-studies/${data.slug}` : "/case-studies",
    image: data?.image,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${SITE_URL}/case-studies/${data.slug}#article`,
              headline: data.title,
              description: data.metaDescription,
              image: data.image,
              datePublished: data.datePublished,
              dateModified: data.dateModified,
              inLanguage: "en-IN",
              author: { "@type": "Organization", name: "Asia Sourcing India", url: SITE_URL },
              publisher: { "@id": `${SITE_URL}#organization` },
              mainEntityOfPage: `${SITE_URL}/case-studies/${data.slug}`,
              articleSection: data.sector,
              about: data.sector,
              keywords: `${data.sector}, ${data.region}, India sourcing case study`,
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/case-studies/${data.slug}#faq`,
              mainEntity: (data.faqs || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            breadcrumb([
              { name: "Home", item: `${SITE_URL}/` },
              { name: "Case Studies", item: `${SITE_URL}/case-studies` },
              { name: data.title, item: `${SITE_URL}/case-studies/${data.slug}` },
            ]),
            {
              "@type": ["Organization", "LocalBusiness"],
              "@id": `${SITE_URL}#organization`,
              name: "Asia Sourcing India",
              url: SITE_URL,
              logo: `${SITE_URL}/assets/og.jpg`,
            },
          ],
        }
      : undefined,
  });

  if (!data) return <Navigate to="/case-studies" replace />;

  const currentUrl = `${SITE_URL}/case-studies/${data.slug}`;

  return (
    <article data-testid="case-study-page">
      {/* HERO */}
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem", maxWidth: "1100px" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p className="mono" style={{ fontSize: ".72rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase" }}>
            <Link to="/" style={{ color: "inherit" }}>Home</Link> ·{" "}
            <Link to="/case-studies" style={{ color: "inherit" }}>Case Studies</Link> ·{" "}
            {data.sector}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="cs-eyebrow">
            {data.eyebrow} · {data.duration} · {data.readTime}
          </p>
        </Reveal>
        <WordReveal as="h1" text={data.title} className="display" stagger={30} testId="cs-h1" />
      </header>

      {data.image && data.image !== "/assets/og/og.jpg" && (
        <div className="wrap" style={{ paddingBlock: "1rem var(--section)", maxWidth: "1200px" }}>
          <Reveal>
            <img
              src={data.image}
              alt={data.imageAlt || data.title}
              width="1600"
              height="900"
              loading="eager"
              style={{ display: "block", width: "100%", height: "auto", maxHeight: "540px", objectFit: "cover", border: "1px solid var(--line)" }}
            />
          </Reveal>
        </div>
      )}

      <div className="wrap" style={{ maxWidth: "1200px", paddingBlock: "1rem" }}>
        <ArticleShare url={currentUrl} title={data.title} />
      </div>

      <div className="wrap" style={{ maxWidth: "820px" }}>
        {/* 1. EXECUTIVE SUMMARY */}
        <Section id="executive-summary" eyebrow="§ 01 · Executive summary" heading="Executive Summary">
          {data.executiveSummary.map((p, i) => <Para key={i}>{p}</Para>)}
        </Section>

        {/* 2. CLIENT PROFILE */}
        <Section id="client-profile" eyebrow="§ 02 · Client profile" heading="Client Profile">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.4rem 2rem" }} data-testid="cs-client-profile">
            {Object.entries(data.clientProfile).map(([k, v]) => (
              <div key={k} style={{ borderTop: "1px solid var(--line)", paddingTop: "1rem" }}>
                <p className="mono" style={{ fontSize: ".64rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                  {k.replace(/([A-Z])/g, " $1").trim()}
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: ".4rem 0 0", color: "var(--ink)" }}>{v}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 3. CHALLENGES */}
        <Section id="challenges" eyebrow="§ 03 · Business challenges" heading="The Business Challenges We Set Out to Solve">
          <Para>{data.challenges.intro}</Para>
          <NamedList items={data.challenges.items} />
        </Section>

        {/* 4. STRATEGY */}
        <Section id="strategy" eyebrow="§ 04 · Our strategy" heading="Our Sourcing Strategy">
          <Para>{data.strategy.intro}</Para>
          <NamedList items={data.strategy.items} />
        </Section>

        {/* 5. PRODUCT DEVELOPMENT */}
        <Section id="product-development" eyebrow="§ 05 · Product development" heading="Product Development">
          <Para>{data.productDevelopment.intro}</Para>
          <NamedList items={data.productDevelopment.items} />
        </Section>

        {/* 6. FACTORY SELECTION */}
        <Section id="factory-selection" eyebrow="§ 06 · Factory selection" heading="Factory Selection">
          <Para>{data.factorySelection.intro}</Para>
          <NamedList items={data.factorySelection.items} />
        </Section>

        {/* 7. PRODUCTION MANAGEMENT */}
        <Section id="production" eyebrow="§ 07 · Production management" heading="Production Management">
          <Para>{data.productionManagement.intro}</Para>
          <NamedList items={data.productionManagement.items} />
        </Section>

        {/* 8. QUALITY ASSURANCE */}
        <Section id="quality" eyebrow="§ 08 · Quality assurance" heading="Quality Assurance">
          <Para>{data.qualityAssurance.intro}</Para>
          <NamedList items={data.qualityAssurance.items} />
        </Section>

        {/* 9. LOGISTICS */}
        <Section id="logistics" eyebrow="§ 09 · Logistics & export" heading="Logistics &amp; Export">
          <Para>{data.logistics.intro}</Para>
          <NamedList items={data.logistics.items} />
        </Section>

        {/* 10. RESULTS */}
        <Section id="results" eyebrow="§ 10 · Results" heading="Measurable Results">
          <Para>{data.results.intro}</Para>
          <div style={{ display: "grid", gap: "1rem", maxWidth: "68ch" }}>
            {data.results.items.map((r, i) => (
              <Reveal key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", borderBottom: "1px solid var(--line)", paddingBottom: "1rem" }}>
                <p className="mono" style={{ fontSize: ".72rem", color: "var(--brass-deep)", letterSpacing: ".12em", textTransform: "uppercase", margin: 0 }}>
                  {r.metric}
                </p>
                <p style={{ color: "var(--ink-soft)", fontSize: ".95rem", margin: 0 }}>{r.from}</p>
                <p style={{ fontFamily: "var(--display)", color: "var(--ink)", fontSize: ".95rem", margin: 0, fontWeight: 500 }}>
                  → {r.to}
                </p>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* 11. WHY WE MADE THE DIFFERENCE */}
        <Section id="why-different" eyebrow="§ 11 · The strategic difference" heading="Why Asia Sourcing India Made the Difference">
          <Para>{data.whyWeMadeTheDifference.intro}</Para>
          <NamedList items={data.whyWeMadeTheDifference.items} />
        </Section>

        {/* 12. KEY METRICS */}
        <Section id="key-metrics" eyebrow="§ 12 · Key metrics" heading="Key Metrics">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", maxWidth: "72ch" }} data-testid="cs-key-metrics">
            {data.metrics.map(([k, v], i) => (
              <div key={i} style={{ border: "1px solid var(--line)", padding: "1.1rem 1rem" }}>
                <p className="mono" style={{ fontSize: ".62rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>{k}</p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: ".35rem 0 0", color: "var(--ink)" }}>{v}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 13. FAQ */}
        <Section id="faq" eyebrow="§ 13 · Frequently asked" heading="Frequently Asked Questions">
          <div style={{ maxWidth: "72ch" }}>
            {data.faqs.map((f, i) => (
              <Reveal key={i} as="details" data-testid={`cs-faq-${i}`} style={{ borderBottom: "1px solid var(--line)", padding: "1.4rem 0" }}>
                <summary style={{ cursor: "pointer", fontFamily: "var(--display)", fontSize: "1.05rem", color: "var(--ink)", listStyle: "none" }}>
                  {f.q}
                </summary>
                <p style={{ marginTop: ".8rem", color: "var(--ink-soft)", lineHeight: 1.7 }}>{f.a}</p>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* 14. RELATED SERVICES */}
        <Section id="related" eyebrow="§ 14 · Related services" heading="Related Services">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
            {(data.relatedServices || []).map((s) => (
              <Link
                key={s}
                to={`/services/${s}`}
                style={{ display: "block", padding: "1rem 1.1rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}
                data-testid={`cs-related-${s}`}
              >
                <p className="mono" style={{ fontSize: ".64rem", color: "var(--brass-deep)", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Service</p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1rem", margin: ".4rem 0 0", textTransform: "capitalize" }}>
                  {s.replace(/-/g, " ")}
                </p>
              </Link>
            ))}
          </div>
        </Section>

        {/* 15. CTA */}
        <Section id="cta" eyebrow="§ 15 · Continue the conversation" heading="Ready to Build Your Own India Sourcing Programme?">
          <Para>
            Every partnership documented on this page began with a written brief. Send yours and we&apos;ll return a factory shortlist and unit-cost model within 7-10 working days — at no charge.
          </Para>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1.6rem" }}>
            <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="cs-cta-contact">
              Start a sourcing brief
            </Link>
            <Link to="/case-studies" className="btn btn-lg" data-testid="cs-cta-back">
              All case studies
            </Link>
          </div>
        </Section>
      </div>

      {/* PREV / NEXT */}
      {(prev || next) && (
        <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)", maxWidth: "1200px" }} data-testid="cs-nav">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.4rem" }}>
            {prev && (
              <Link to={`/case-studies/${prev.slug}`} data-testid="cs-prev" style={{ display: "block", padding: "1.4rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit" }}>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>← Previous case study</p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: ".5rem 0 0", lineHeight: 1.3 }}>{prev.title}</p>
              </Link>
            )}
            {next && (
              <Link to={`/case-studies/${next.slug}`} data-testid="cs-next" style={{ display: "block", padding: "1.4rem", border: "1px solid var(--line)", textDecoration: "none", color: "inherit", textAlign: "right" }}>
                <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>Next case study →</p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: ".5rem 0 0", lineHeight: 1.3 }}>{next.title}</p>
              </Link>
            )}
          </div>
        </section>
      )}

      <NewsletterSubscribe />
    </article>
  );
};

export default CaseStudyDetail;
